import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  timeKeepingTemplateGetAll,
  timeKeepingTemplateExcel,
} from '../../../redux/slices/project.slice'
import {projectLookup, basicLookup} from '../../../redux/slices/lookup.slice'

import {pagination} from '../../../helpers'

import {useMemoizedStaticData} from '../../../utils/performanceOptimizations'

const selectData = (state) => ({
  timekeepingtemplate: state.project.timekeepingtemplate,
  loading: state.project.loading,
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  TimeKeepingType: state.lookup.TimeKeepingType,
})

function List() {
  const intl = useIntl()
  const {timekeepingtemplate, loading} = useSelector(selectData)
  const {projectLookupList, TimeKeepingType} = useSelector(selectLookupData)
  const dispatch = useDispatch()
  const pageKey = 'TIMEKEEPINGTEMPLATE'

  const [params, setParams] = useState(pagination)

  useEffect(() => {
    dispatch(timeKeepingTemplateGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(basicLookup('TimeKeepingType'))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleFilterSubmit = useCallback((values) => {
    const {filterName, ...rest} = values
    setParams({...rest, name: filterName || values.name})
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      timeKeepingTemplateExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.TIMEKEEPINGTEMPLATE'}),
      })
    )
  }, [dispatch, params, intl])

  const columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'TIMEKEEPINGTEMPLATE.SEARCH.NAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'name',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'TIMEKEEPINGTEMPLATE.SEARCH.PROJECTNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'projectName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'TIMEKEEPINGTEMPLATE.SEARCH.TIMEKEEPINGTYPENAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'timeKeepingTypeName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'TIMEKEEPINGTEMPLATE.SEARCH.DATAORDER'})}
            className='min-w-125px'
          />
        ),
        accessor: 'dataOrder',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
    ],
    [intl, handleSorting]
  )

  const actionButtons = useMemoizedStaticData(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handleExcelExport,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
      {
        title: 'FILTER',
        icon: '/images/icons/duotune/general/gen031.svg',
        variant: 'light-primary',
        list: [
          {
            title: 'TIMEKEEPINGTEMPLATE.SEARCH.PROJECTNAME',
            data: projectLookupList,
            name: 'projectId',
          },
          {
            title: 'TIMEKEEPINGTEMPLATE.SEARCH.TIMEKEEPINGTYPENAME',
            data: TimeKeepingType,
            name: 'timeKeepingTypeId',
          },
          {
            title: 'TIMEKEEPINGTEMPLATE.SEARCH.NAME',
            type: 'text',
            name: 'filterName',
          },
        ],
      },
    ],
    [handleExcelExport, projectLookupList, TimeKeepingType]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.TIMEKEEPINGTEMPLATE'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={pageKey}
        />
        <KTCardBody>
          {!timekeepingtemplate && <ListLoading />}
          {timekeepingtemplate && (
            <Table
              items={timekeepingtemplate}
              cols={columns}
              handlePagination={handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
